/**
 * Secure Permission Cache - Performance + Security (Refactored)
 *
 * Provides encrypted, tamper-resistant caching for permission states
 * with comprehensive security measures against cache poisoning attacks.
 *
 * Security Features:
 * - AES-GCM encryption using Web Crypto API
 * - Cache integrity verification
 * - TTL-based expiration (max 5 minutes)
 * - Memory-safe cleanup
 * - Cache poisoning prevention
 * - Automatic cache invalidation on security events
 */

import { PermissionState } from './permission-integrity';
import { systemLogger } from '@/lib/logging';

// Import extracted modules
import { CacheEncryption } from './cache/cache-encryption';
import { CacheIntegrity } from './cache/cache-integrity';
import { CacheManagement } from './cache/cache-management';
import { CacheStatsManager } from './cache/cache-stats';
import { CacheSecurity } from './cache/cache-security';

// Import types
import type { CacheEntry, CacheStats, CacheConfig } from './types/cache-types';

class SecurePermissionCache {
  private cache: Map<string, CacheEntry> = new Map();
  private config: CacheConfig;

  // Extracted modules
  private encryption: CacheEncryption;
  private integrity: CacheIntegrity;
  private management: CacheManagement;
  private stats: CacheStatsManager;
  private security: CacheSecurity;

  constructor(config?: Partial<CacheConfig>) {
    this.config = {
      maxEntries: 1000,
      defaultTTL: 5 * 60 * 1000, // 5 minutes
      maxTTL: 10 * 60 * 1000, // 10 minutes max
      encryptionAlgorithm: 'AES-GCM',
      cleanupInterval: 60 * 1000, // 1 minute
      enableIntegrityChecks: false, // Disabled due to signature verification issues (GitHub Issue #1)
      ...config
    };

    // Initialize modules
    this.encryption = new CacheEncryption();
    this.integrity = new CacheIntegrity();
    this.management = new CacheManagement(this.config);
    this.stats = new CacheStatsManager();
    this.security = new CacheSecurity();

    // Setup cache management
    this.management.startCleanupTimer(this.cache);
    this.security.setupSecurityEventListener(this.cache);
  }

  /**
   * Check if cache should be bypassed for security reasons
   *
   * IMPLEMENTATION NOTE: Cache bypass is currently disabled because:
   * 1. Signature verification has issues with security key synchronization
   * 2. False positives were causing "Security violation detected" messages
   * 3. The integrity checking in PermissionGate.tsx is also disabled
   *
   * GitHub Issue #1: Implement proper security key management and re-enable:
   * - Fix signature generation/verification consistency
   * - Implement proper cache invalidation on key rotation
   * - Add System Admin detection for enhanced security bypass
   */
  private shouldBypassCache(_permissionState: PermissionState): boolean {
    // Cache bypass disabled - see implementation note above
    return false;
  }

  /**
   * Store permission state in encrypted cache
   */
  async set(
    key: string,
    data: PermissionState,
    userId: string,
    customTTL?: number
  ): Promise<void> {
    const startTime = performance.now();

    try {
      // Validate cache access
      if (!this.security.validateCacheAccess(userId, key)) {
        throw new Error('Unauthorized cache access');
      }

      // Check if cache should be bypassed
      if (this.shouldBypassCache(data)) {
        systemLogger.debug('Cache bypassed for security reasons', { key, userId });
        return;
      }

      // Evict entries if necessary
      const evictedCount = this.management.evictIfNecessary(this.cache);
      if (evictedCount > 0) {
        this.stats.recordEviction(evictedCount);
      }

      // Prepare data for caching
      const timestamp = Date.now();
      const ttl = Math.min(customTTL || this.config.defaultTTL, this.config.maxTTL);

      // Encrypt the data
      const dataString = JSON.stringify(data);
      const encrypted = await this.encryption.encrypt(dataString);

      // Create integrity checksum
      const checksum = await this.integrity.createChecksum(
        dataString,
        userId,
        timestamp,
        encrypted
      );

      // Create cache entry
      const entry: CacheEntry = {
        data,
        encrypted: encrypted.data,
        iv: encrypted.iv,
        tag: encrypted.tag,
        timestamp,
        ttl,
        accessCount: 0,
        userId,
        checksum,
      };

      // Store in cache
      this.cache.set(key, entry);

      // Record performance
      const accessTime = performance.now() - startTime;
      this.stats.recordHit(accessTime);

      systemLogger.debug('Permission cached successfully', {
        key,
        userId,
        ttl,
        cacheSize: this.cache.size,
      });

    } catch (error) {
      const accessTime = performance.now() - startTime;
      this.stats.recordMiss(accessTime);

      this.security.handleSecurityError(
        error as Error,
        'cache_set',
        userId,
        this.cache
      );

      systemLogger.error('Failed to cache permission:', error);
      throw error;
    }
  }

  /**
   * Retrieve permission state from cache
   */
  async get(key: string, userId: string): Promise<PermissionState | null> {
    const startTime = performance.now();

    try {
      // Validate cache access
      if (!this.security.validateCacheAccess(userId, key)) {
        throw new Error('Unauthorized cache access');
      }

      const entry = this.cache.get(key);
      if (!entry) {
        this.stats.recordMiss(performance.now() - startTime);
        return null;
      }

      // Verify user ownership
      if (entry.userId !== userId) {
        this.security.handleSecurityError(
          new Error('Cache entry user mismatch'),
          'cache_get',
          userId,
          this.cache
        );
        return null;
      }

      // Check expiration
      if (this.integrity.isExpired(entry)) {
        this.cache.delete(key);
        this.stats.recordMiss(performance.now() - startTime);
        return null;
      }

      // Verify integrity if enabled
      if (this.config.enableIntegrityChecks) {
        const isValid = await this.integrity.verifyIntegrity(entry);
        if (!isValid) {
          this.cache.delete(key);
          this.security.handleSecurityError(
            new Error('Cache integrity check failed'),
            'cache_get',
            userId,
            this.cache
          );
          return null;
        }
      }

      // Decrypt data
      const decrypted = await this.encryption.decrypt({
        data: entry.encrypted,
        iv: entry.iv,
        tag: entry.tag,
      });

      const data = JSON.parse(decrypted) as PermissionState;

      // Update access count
      entry.accessCount++;
      this.cache.set(key, entry);

      // Record performance
      const accessTime = performance.now() - startTime;
      this.stats.recordHit(accessTime);

      systemLogger.debug('Permission retrieved from cache', {
        key,
        userId,
        accessCount: entry.accessCount,
      });

      return data;

    } catch (error) {
      const accessTime = performance.now() - startTime;
      this.stats.recordMiss(accessTime);

      this.security.handleSecurityError(
        error as Error,
        'cache_get',
        userId,
        this.cache
      );

      systemLogger.error('Failed to retrieve from cache:', error);
      return null;
    }
  }

  /**
   * Remove entry from cache
   */
  delete(key: string, userId: string): boolean {
    try {
      if (!this.security.validateCacheAccess(userId, key)) {
        return false;
      }

      const deleted = this.cache.delete(key);
      if (deleted) {
        systemLogger.debug('Cache entry deleted', { key, userId });
      }

      return deleted;
    } catch (error) {
      systemLogger.error('Failed to delete cache entry:', error);
      return false;
    }
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.management.clearAll(this.cache);
    this.stats.resetStats();
  }

  /**
   * Clear entries for specific user
   */
  clearUser(userId: string): void {
    this.management.clearUserEntries(this.cache, userId);
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    const memoryUsage = this.management.estimateMemoryUsage(this.cache);
    return this.stats.getStats(this.cache.size, memoryUsage);
  }

  /**
   * Get performance health check
   */
  getHealthCheck(): {
    healthy: boolean;
    issues: string[];
    recommendations: string[];
    stats: CacheStats;
  } {
    const health = this.stats.isPerformanceHealthy();
    const stats = this.getStats();

    return {
      ...health,
      stats,
    };
  }

  /**
   * Cleanup cache resources
   */
  cleanup(): void {
    this.management.stopCleanupTimer();
    this.security.cleanup();
    this.clear();
  }
}

// Export singleton instance
export const securePermissionCache = new SecurePermissionCache({
  maxEntries: 2000,
  defaultTTL: 5 * 60 * 1000, // 5 minutes
  enableIntegrityChecks: false, // Will be enabled when Issue #1 is resolved
});

// Re-export types and classes for external use
export type { CacheEntry, CacheStats, CacheConfig };
export { SecurePermissionCache };
export default securePermissionCache;